// src/app/core/tockensPaymentLogic/ProfileViewModel.ts
import { makeObservable, observable, action, computed, runInAction } from "mobx";
import { SingletonClass } from "@/app/utils/SingletonClass";
import { TelegramAuthController } from "@/app/shared/auth/TelegramAuthController";
import { PaymentsApi } from '@/app/data/profilPayment/api/PaymentsApi';
import { NetworkError } from "@/app/shared/network";
import { AuthApi } from "@/app/data/auth/AuthApi";

export interface AppSubscription {
  id: number;
  userId: number;
  level: string;
  price: number;
  description?: string | null;
  isActive: boolean;
  startDate: string;
  endDate?: string | null;
  durationDays: number;
  advantages: Record<string, unknown>;
  isValid: boolean;
  status: string;
  tokensRenewPerDay?: number | null;
}

export interface ProfileData {
  telegramId: number;
  firstName: string;
  lastName?: string;
  userName?: string;
  currentTokens: number;
  appSubscription: AppSubscription;
  telegramPremium: boolean;
  subscriptionLevel: string;
  isPremium?: boolean;
  photoUrl?: string;
  referralCode?: string;
  defaultModel?: string;
  defaultImageModel?: string;
  defaultVideoModel?: string;
}

export class ProfileViewModel extends SingletonClass {
  private static _instance: ProfileViewModel;
  private telegramAuthController = TelegramAuthController.getInstance();
  private paymentsApi = new PaymentsApi();

  profile: ProfileData | null = null;
  balance: number | null = null;
  subscription: AppSubscription | null = null;
  isLoadingProfile = false;
  isLoadingBalance = false;
  isLoadingSubscription = false;
  profileError = "";
  balanceError = "";
  subscriptionError = "";

  static getInstance(): ProfileViewModel {
    if (!ProfileViewModel._instance) {
      ProfileViewModel._instance = new ProfileViewModel();
    }
    return ProfileViewModel._instance;
  }

  constructor() {
    super();
    makeObservable(this, {
      profile: observable,
      balance: observable,
      subscription: observable,
      isLoadingProfile: observable,
      isLoadingBalance: observable,
      isLoadingSubscription: observable,
      profileError: observable,
      balanceError: observable,
      subscriptionError: observable,
      user: computed,
      displayName: computed,
      displayUsername: computed,
      currentSubscription: computed,
      isTelegramPremium: computed,
      loadProfile: action,
      loadBalance: action,
      loadSubscription: action,
      loadAll: action,
    });
  }

  private getErrorMessage(error: unknown): string {
    if (error instanceof NetworkError) return error.message;
    if (error instanceof Error) return error.message;
    return "Не удалось загрузить данные";
  }

  async loadProfile(): Promise<void> {
    runInAction(() => {
      this.isLoadingProfile = true;
      this.profileError = "";
    });

    const tgUser = this.telegramAuthController.user;

    try {
      const me = await this.paymentsApi.getMe();
      console.log('[ProfileViewModel] /client/me ответ:', me);

      runInAction(() => {
      this.profile = {
        telegramId: me.userId,
        firstName: (me as any).firstName || tgUser?.first_name || 'User',
        lastName: (me as any).lastName || tgUser?.last_name || '',
        userName: (me as any).userName || tgUser?.username || '',
        currentTokens: this.balance ?? 0,
        appSubscription: {} as AppSubscription,
        telegramPremium: tgUser?.is_premium || false,
        subscriptionLevel: '',
        photoUrl: (me as any).photoUrl || tgUser?.photo_url || '',
        // 🔑 новые поля
        referralCode: me.referralCode ?? undefined,
        defaultModel: me.defaultModel ?? undefined,
        defaultImageModel: me.defaultImageModel ?? undefined,
        defaultVideoModel: me.defaultVideoModel ?? undefined,
      };
      this.isLoadingProfile = false;
    });
    } catch (error) {
      console.warn('[ProfileViewModel] /client/me упал, используем Telegram');
      runInAction(() => {
        this.profile = {
          telegramId: tgUser?.id ?? 0,
          firstName: tgUser?.first_name || 'User',
          lastName: tgUser?.last_name || '',
          userName: tgUser?.username || '',
          currentTokens: this.balance ?? 0,
          appSubscription: {} as AppSubscription,
          telegramPremium: tgUser?.is_premium || false,
          subscriptionLevel: '',
          photoUrl: tgUser?.photo_url || '',
        };
        this.isLoadingProfile = false;
        this.profileError = '';
      });
    }
  }

  async loadBalance(): Promise<void> {
    runInAction(() => {
      this.isLoadingBalance = true;
      this.balanceError = "";
    });

    try {
      const balance = await this.paymentsApi.getBalance();
      runInAction(() => {
        this.balance = balance.currentTokens;
        if (this.profile) {
          this.profile.currentTokens = balance.currentTokens;
        }
        this.isLoadingBalance = false;
      });
    } catch (error) {
      runInAction(() => {
        this.balanceError = this.getErrorMessage(error);
        this.isLoadingBalance = false;
      });
    }
  }

  async loadSubscription(): Promise<void> {
    this.isLoadingSubscription = false;
  }

  async loadAll(): Promise<void> {
    // Ждём токен до 5 секунд — на случай гонки с WS
      let attempts = 0;
      while (!AuthApi.getAccessToken() && attempts < 50) {
          await new Promise((r) => setTimeout(r, 100));
          attempts += 1;
      }
      if (!AuthApi.getAccessToken()) {
          console.warn('[ProfileViewModel] no token after wait, skip');
          return;
      }
      await Promise.all([this.loadProfile(), this.loadBalance()]);
  }

  get user() {
    return this.telegramAuthController.user;
  }

  get displayName(): string {
    if (this.profile) {
      const first = this.profile.firstName || this.user?.first_name || '';
      const last = this.profile.lastName || this.user?.last_name || '';
      return `${first}${last ? ` ${last}` : ''}`.trim() || 'Пользователь';
    }
    return 'Пользователь';
  }

  get displayUsername(): string {
    return this.profile?.userName || this.user?.username || '';
  }

  get currentSubscription(): AppSubscription | null {
    return this.subscription || this.profile?.appSubscription || null;
  }

  get isTelegramPremium(): boolean {
    return this.profile?.telegramPremium || this.user?.is_premium || false;
  }
}